import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  File as FileIcon,
  type LucideIcon,
} from "lucide-react";
import { apiFetchBlob } from "@/lib/api";

export interface FileThumbnailProps {
  /** Authenticated thumbnail endpoint, or null when the row has none — renders a
   *  per-type icon and issues no request at all. */
  url: string | null;
  contentType: string | null;
  /** Original file name, used for accessible alt text. */
  alt: string;
}

/**
 * The one thumbnail renderer for every file grid. Bearer-token endpoints cannot feed a
 * bare <img src> (see artwork-thumbnail.tsx, the precedent), so bytes are fetched via
 * apiFetchBlob and exposed through an object URL, revoked on unmount.
 */
export function FileThumbnail({ url, contentType, alt }: FileThumbnailProps) {
  const query = useQuery({
    queryKey: ["file-thumbnail", url],
    queryFn: () => apiFetchBlob(url!),
    enabled: url != null,
    staleTime: Infinity, // job/quote thumbnails are immutable; a replacement invalidates by query-key prefix
    retry: false,
  });

  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!query.data) return;
    const created = URL.createObjectURL(query.data);
    setObjectUrl(created);
    return () => {
      URL.revokeObjectURL(created);
      setObjectUrl(null);
    };
  }, [query.data]);

  if (url != null && objectUrl) {
    return <img src={objectUrl} alt={alt} className="h-8 w-8 rounded border object-cover" />;
  }
  if (url != null && !query.isError) {
    // Fetch in flight — hold the slot without flashing an icon that is about to vanish.
    return <div className="h-8 w-8 rounded bg-muted" aria-hidden />;
  }

  const Icon = typeIcon(contentType);
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded bg-muted" title={alt}>
      <Icon className="h-5 w-5 text-muted-foreground" aria-hidden />
    </div>
  );
}

function typeIcon(contentType: string | null): LucideIcon {
  const c = contentType ?? "";
  if (c.startsWith("image/")) return FileImage;
  if (c === "application/pdf") return FileText;
  if (c === "application/zip") return FileArchive;
  if (c.includes("spreadsheet")) return FileSpreadsheet;
  return FileIcon;
}
